import React, { useEffect, useMemo, useState } from "react";
import { Info } from "lucide-react";
import uber from "../../src/assests/uber-icon.svg";
import doorDash from "../../src/assests/DASH.svg";
import { useAuth } from "../context/AuthContext";
import { useDelivery } from "../context/DeliveryContext";
import { useOrder } from "../context/OrderContext";

const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({
  text,
  children,
}) => {
  const [visible, setVisible] = useState(false);

  return (
    <span
      className="relative flex items-center"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible && (
        <span className="absolute text-[9px] sm:text-[10px] -translate-x-[20%] md:-translate-x-[0] lg:-translate-x-[20%] top-6 bg-black text-white  py-1 px-2 rounded shadow-lg whitespace-nowrap z-10">
          {text}
        </span>
      )}
    </span>
  );
};

interface BillDetails {
  itemTotal: number;
  deliveryFee: number;
  toPay: number;
  tipAmount: number;
}
type DealType = 'percentage_discount' | 'fixed_price' | 'buy_x_get_y' | 'build_your_own' | 'bundle';

interface AppliedDeal {
  dealId: string;
  originalDealId?: string;
  name: string;
  discountAmount: number;
  dealType: DealType;
}

const BillDetailsCard: React.FC<{
  bill: BillDetails;
  platform?: "uber" | "doordash" | "custom" | null;
  deliveryTax: number;
  orderType?: "delivery" | "pickup";
  discountValue?: number;
  totalDealDiscount?: number;
  bonusAmountUsed?: number;
  itemTax?: number;
  // Canadian tax split of the item tax; omit for single-rate (US) taxes
  gstAmount?: number;
  pstAmount?: number;
}> = ({ bill, platform, deliveryTax, orderType: orderTypeProp, discountValue = 0, totalDealDiscount: passedDealDiscount = 0, bonusAmountUsed = 0, itemTax = 0, gstAmount, pstAmount }) => {
  const [showDeliveryTooltip, setShowDeliveryTooltip] = useState(false);
  const { user, currencySymbol } = useAuth();
  const { orderData, setTotalDealDiscount } = useOrder();
  const [appliedDeals, setAppliedDeals] = useState<AppliedDeal[]>([]);
  // deliveryFee from API is the BASE fee (before tax)
  const baseDeliveryFee = bill.deliveryFee;
  const tipAmount = bill.tipAmount || 0;
  const deliveryTaxAmount = baseDeliveryFee > 0 ? (baseDeliveryFee * deliveryTax) / 100 : 0;
  const totalDeliveryFee = baseDeliveryFee + deliveryTaxAmount;
  const totalDealDiscount = passedDealDiscount;
  const { orderType } = useDelivery();
  const currentOrderType = orderTypeProp || orderType;
  // bill.itemTotal is pre-tax; itemTax is the item tax actually charged
  // (already reduced for the bonus-covered portion when bonus is used).
  // Scale GST/PST to the tax actually charged (bonus can reduce it) and give
  // PST the rounding remainder so the two rows always add up to Taxes.
  const hasTaxSplit = gstAmount != null || pstAmount != null;
  const rawTaxSplit = (gstAmount ?? 0) + (pstAmount ?? 0);
  const gstShown = rawTaxSplit > 0 ? Number(((itemTax * (gstAmount ?? 0)) / rawTaxSplit).toFixed(2)) : 0;
  const pstShown = Number((itemTax - gstShown).toFixed(2));
  const finalToPay = Math.max(
    0,
    bill.itemTotal +
      itemTax -
      bonusAmountUsed -
      discountValue -
      totalDealDiscount +
      tipAmount +
      (currentOrderType === "delivery" ? totalDeliveryFee : 0),
  );
  useEffect(() => {
    if (orderData?.appliedDeals) {
      setAppliedDeals(orderData?.appliedDeals);
    }
  }, [orderData?.appliedDeals]);
  useEffect(() => {
    setTotalDealDiscount(totalDealDiscount);
  }, [totalDealDiscount, setTotalDealDiscount]);

  return (
    <div className="bg-white rounded-lg border-[1px] border-black/10 p-4 dm-bill-card">
      <h3 className="font-semibold text-[19px] text-black mb-4">
        Bill Details
      </h3>

      <div className="space-y-3">
        <div className="flex justify-between text-[14px]">
          <span className="text-black">Item Total</span>
          <span className="text-black">{currencySymbol}{bill.itemTotal.toFixed(2)}</span>
        </div>
        {itemTax > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-black">Taxes</span>
            <span className="text-black">+{currencySymbol}{itemTax.toFixed(2)}</span>
          </div>
        )}
        {itemTax > 0 && hasTaxSplit && (
          <>
            <div className="flex justify-between text-[13px] pl-3 text-black/60">
              <span>GST</span>
              <span>{currencySymbol}{gstShown.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-[13px] pl-3 text-black/60">
              <span>PST</span>
              <span>{currencySymbol}{pstShown.toFixed(2)}</span>
            </div>
          </>
        )}
        {bonusAmountUsed > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-green-600">Bonus Balance Discount</span>
            <span className="text-green-600">-{currencySymbol}{bonusAmountUsed.toFixed(2)}</span>
          </div>
        )}
        {discountValue > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-black">Discount</span>
            <span className="text-black">
              -{currencySymbol}{discountValue.toFixed(2)}
            </span>
          </div>
        )}
        {totalDealDiscount > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-black">Deal Discount</span>
            <span className="text-black">-{currencySymbol}{totalDealDiscount.toFixed(2)}</span>
          </div>
        )}
        {tipAmount > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-black">Tip</span>
            <span className="text-black">
              {currencySymbol}{tipAmount.toFixed(2)}
            </span>
          </div>
        )}

        {/* For DELIVERY - show combined delivery fee + tip */}
        {currentOrderType === "delivery" && (
          <div className="flex justify-between text-[14px] items-center">
            <div className="flex items-center gap-2">
              {platform === "doordash" && (
                <img src={doorDash} alt="DoorDash" className="w-8 h-8" />
              )}
              {platform === "uber" && (
                <img src={uber} alt="Uber Eats" className="w-8 h-8" />
              )}
              <span className="text-black flex items-center gap-1">
                Delivery Fee 
                <Tooltip text="Includes delivery fee, taxes, and driver tip">
                  <Info className="w-3 h-3 text-gray-400 cursor-pointer" />
                </Tooltip>
              </span>
            </div>

            {/* Delivery Fee with Breakdown Tooltip */}
            {platform === "custom" && !bill.deliveryFee ? (
              <span className="text-black">Free</span>
            ) : bill.deliveryFee || tipAmount > 0 ? (
              <div
                className="relative flex items-center gap-1"
                onMouseEnter={() => setShowDeliveryTooltip(true)}
                onMouseLeave={() => setShowDeliveryTooltip(false)}
              >
                <span className="text-black">
                  {currencySymbol}{(totalDeliveryFee).toFixed(2)}
                </span>
                <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />

                {/* Tooltip */}
                {showDeliveryTooltip && (
                  <div className="absolute top-full right-0 mt-1 bg-white border-[1px] border-black/10 rounded-lg shadow-lg p-3 z-10 w-48 dm-bill-card">
                    <div className="text-xs space-y-1.5">
                      {baseDeliveryFee > 0 && (
                        <div className="flex justify-between text-[14px] text-black/70">
                          <span>Base delivery fee</span>
                          <span>{currencySymbol}{baseDeliveryFee.toFixed(2)}</span>
                        </div>
                      )}
                      {deliveryTaxAmount > 0 && (
                        <div className="flex justify-between text-black/70 text-[14px]">
                          <span>Taxes & charges ({deliveryTax}%)</span>
                          <span>{currencySymbol}{deliveryTaxAmount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="border-t-[1px] border-black/10 pt-1.5 mt-1.5"></div>
                      <div className="flex justify-between font-semibold text-gray-900">
                        <span>Total</span>
                        <span>{currencySymbol}{(totalDeliveryFee).toFixed(2)}</span>
                      </div>
                    </div>
                    {/* Triangle pointer */}
                    <div className="absolute -top-1 right-4 w-2 h-2 bg-white border-l border-t border-black/10 transform rotate-45"></div>
                  </div>
                )}
              </div>
            ) : (
              <span className="text-black">-/-</span>
            )}
          </div>
        )}
        <hr className="my-3" />

        <div className="flex justify-between text-black font-semibold text-[15px]">
          <span>TO PAY</span>
          <span>{currencySymbol}{finalToPay.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
};

export default BillDetailsCard; 